import * as Joi from 'joi';
import { BillingCycle } from '../../../../enums/entity.enums';
import { Currency } from '../../../../enums/retrieve-transaction.enums';
const stringWithNumbersOnlyRegEx = /^\d+$/; // @ref https://github.com/sideway/joi/issues/1157#issuecomment-293538673

export const CreateMerchantSchema = Joi.object({
  partnerId: Joi.number().optional().messages({
    'any.only': 'Partner is invalid.',
    'number.base': 'Partner is invalid.',
  }),
  resellerId: Joi.number().optional().messages({
    'any.only': 'Reseller is invalid.',
    'number.base': 'Reseller is invalid.',
  }),
  entityName: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Merchant name is required.',
    'string.base': 'Merchant name is invalid.',
    'string.empty': 'Merchant name is invalid.',
    'string.min': 'Merchant name can be at most 64 characters only.',
    'string.max': 'Merchant name can be at most 64 characters only.',
  }),
  email: Joi.string().trim().max(100).required().messages({
    'any.required': 'Email is required.',
    'string.base': 'Email is invalid.',
    'string.empty': 'Email is invalid.',
    'string.min': 'Email must have at most 100 characters only.',
    'string.max': 'Email must have at most 100 characters only.',
  }),

  firstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name must have at most 100 characters only.',
    'string.max': 'First name must have at most 100 characters only.',
  }),
  middleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name must have at most 100 characters only.',
    'string.max': 'Middle name must have at most 100 characters only.',
  }),
  noMiddleName: Joi.any(),
  lastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name must have at most 100 characters only.',
    'string.max': 'Last name must have at most 100 characters only.',
  }),
  username: Joi.string().trim().min(1).max(50).required().messages({
    'any.required': 'Username is required.',
    'string.base': 'Username is invalid.',
    'string.empty': 'Username is invalid.',
    'string.min': 'Username must have at most 50 characters only.',
    'string.max': 'Username must have at most 50 characters only.',
  }),
  // 22 characters for password results to 44 character ciphertext
  // Note: Database password max characters is 50
  password: Joi.string()
    .trim()
    .min(8)
    .max(22)
    .pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{7,}$/)
    .required()
    .messages({
      'any.required': 'Password is required.',
      'string.base': 'Password is invalid.',
      'string.pattern.base':
        'Password must have at least 1 uppercase and lowercase character, a number, and special character.',
      'string.empty': 'Password is invalid.',
      'string.min': 'Password must have 8 to 22 characters only.',
      'string.max': 'Password must have 8 to 22 characters only.',
    }),
  confirmPassword: Joi.any().valid(Joi.ref('password')).messages({
    'any.only': 'Passwords do not match.',
  }),

  billingCycleId: Joi.number()
    .valid(
      BillingCycle.WEEKLY,
      BillingCycle.BI_WEEKLY,
      BillingCycle.MONTHLY,
      BillingCycle.QUARTERLY,
      BillingCycle.ANNUALLY
    )
    .required()
    .messages({
      'any.only': 'Billing cycle is invalid.',
      'any.required': 'Billing cycle is required.',
      'number.base': 'Billing cycle is invalid.',
    }),
  currency: Joi.number().valid(Currency.PHP, Currency.USD).required().messages({
    'any.only': 'Currency is invalid.',
    'any.required': 'Currency is required.',
    'number.base': 'Currency is invalid.',
  }),
  pin: Joi.string()
    .trim()
    .length(4)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Pin is required.',
      'string.base': 'Pin is invalid.',
      'string.empty': 'Pin is invalid.',
      'string.pattern.base': 'Pin is invalid.',
      'string.length': 'Pin must have 4 characters only.',
    }),
});

export const AdminCreateMerchantSchema = Joi.object({
  partnerId: Joi.number().required().messages({
    'any.only': 'Partner is invalid.',
    'any.required': 'Partner is required.',
    'number.base': 'Partner is invalid.',
  }),
  resellerId: Joi.number().required().messages({
    'any.only': 'Reseller is invalid.',
    'any.required': 'Reseller is required.',
    'number.base': 'Reseller is invalid.',
  }),
  entityName: Joi.string().trim().min(1).max(500).required().messages({
    'any.required': 'Merchant name is required.',
    'string.base': 'Merchant name is invalid.',
    'string.empty': 'Merchant name is invalid.',
    'string.min': 'Merchant name can be at most 500 characters only.',
    'string.max': 'Merchant name can be at most 500 characters only.',
  }),
  email: Joi.string().trim().max(100).required().messages({
    'any.required': 'Email is required.',
    'string.base': 'Email is invalid.',
    'string.empty': 'Email is invalid.',
    'string.min': 'Email must have at most 100 characters only.',
    'string.max': 'Email must have at most 100 characters only.',
  }),

  firstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name must have at most 100 characters only.',
    'string.max': 'First name must have at most 100 characters only.',
  }),
  middleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name must have at most 100 characters only.',
    'string.max': 'Middle name must have at most 100 characters only.',
  }),
  noMiddleName: Joi.any(),
  lastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name must have at most 100 characters only.',
    'string.max': 'Last name must have at most 100 characters only.',
  }),
  username: Joi.string().trim().min(1).max(50).required().messages({
    'any.required': 'Username is required.',
    'string.base': 'Username is invalid.',
    'string.empty': 'Username is invalid.',
    'string.min': 'Username must have at most 50 characters only.',
    'string.max': 'Username must have at most 50 characters only.',
  }),
  // 22 characters for password results to 44 character ciphertext
  // Note: Database password max characters is 50
  password: Joi.string()
    .trim()
    .min(8)
    .max(22)
    .pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{7,}$/)
    .required()
    .messages({
      'any.required': 'Password is required.',
      'string.base': 'Password is invalid.',
      'string.pattern.base':
        'Password must have at least 1 uppercase and lowercase character, a number, and special character.',
      'string.empty': 'Password is invalid.',
      'string.min': 'Password must have 8 to 22 characters only.',
      'string.max': 'Password must have 8 to 22 characters only.',
    }),
  confirmPassword: Joi.any().valid(Joi.ref('password')).messages({
    'any.only': 'Passwords do not match.',
  }),

  billingCycleId: Joi.number()
    .valid(
      BillingCycle.WEEKLY,
      BillingCycle.BI_WEEKLY,
      BillingCycle.MONTHLY,
      BillingCycle.QUARTERLY,
      BillingCycle.ANNUALLY
    )
    .required()
    .messages({
      'any.only': 'Billing cycle is invalid.',
      'any.required': 'Billing cycle is required.',
      'number.base': 'Billing cycle is invalid.',
    }),
  currency: Joi.number().valid(Currency.PHP, Currency.USD).required().messages({
    'any.only': 'Currency is invalid.',
    'any.required': 'Currency is required.',
    'number.base': 'Currency is invalid.',
  }),
  pin: Joi.string()
    .trim()
    .length(4)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Pin is required.',
      'string.base': 'Pin is invalid.',
      'string.empty': 'Pin is invalid.',
      'string.pattern.base': 'Pin is invalid.',
      'string.length': 'Pin must have 4 characters only.',
    }),
});

export const PartnerCreateMerchantSchema = Joi.object({
  partnerId: Joi.number().optional().messages({
    'any.only': 'Partner is invalid.',
    'number.base': 'Partner is invalid.',
  }),
  resellerId: Joi.number().required().messages({
    'any.only': 'Reseller is invalid.',
    'any.required': 'Reseller is required.',
    'number.base': 'Reseller is invalid.',
  }),
  entityName: Joi.string().trim().min(1).max(500).required().messages({
    'any.required': 'Merchant name is required.',
    'string.base': 'Merchant name is invalid.',
    'string.empty': 'Merchant name is invalid.',
    'string.min': 'Merchant name can be at most 500 characters only.',
    'string.max': 'Merchant name can be at most 500 characters only.',
  }),
  email: Joi.string().trim().max(100).required().messages({
    'any.required': 'Email is required.',
    'string.base': 'Email is invalid.',
    'string.empty': 'Email is invalid.',
    'string.min': 'Email must have at most 100 characters only.',
    'string.max': 'Email must have at most 100 characters only.',
  }),

  firstName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'First name is required.',
    'string.base': 'First name is invalid.',
    'string.empty': 'First name is invalid.',
    'string.min': 'First name must have at most 100 characters only.',
    'string.max': 'First name must have at most 100 characters only.',
  }),
  middleName: Joi.string().trim().min(1).max(100).optional().messages({
    'string.base': 'Middle name is invalid.',
    'string.empty': 'Middle name is invalid.',
    'string.min': 'Middle name must have at most 100 characters only.',
    'string.max': 'Middle name must have at most 100 characters only.',
  }),
  noMiddleName: Joi.any(),
  lastName: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'Last name is required.',
    'string.base': 'Last name is invalid.',
    'string.empty': 'Last name is invalid.',
    'string.min': 'Last name must have at most 100 characters only.',
    'string.max': 'Last name must have at most 100 characters only.',
  }),
  username: Joi.string().trim().min(1).max(50).required().messages({
    'any.required': 'Username is required.',
    'string.base': 'Username is invalid.',
    'string.empty': 'Username is invalid.',
    'string.min': 'Username must have at most 50 characters only.',
    'string.max': 'Username must have at most 50 characters only.',
  }),
  // 22 characters for password results to 44 character ciphertext
  // Note: Database password max characters is 50
  password: Joi.string()
    .trim()
    .min(8)
    .max(22)
    .pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{7,}$/)
    .required()
    .messages({
      'any.required': 'Password is required.',
      'string.base': 'Password is invalid.',
      'string.pattern.base':
        'Password must have at least 1 uppercase and lowercase character, a number, and special character.',
      'string.empty': 'Password is invalid.',
      'string.min': 'Password must have 8 to 22 characters only.',
      'string.max': 'Password must have 8 to 22 characters only.',
    }),
  confirmPassword: Joi.any().valid(Joi.ref('password')).messages({
    'any.only': 'Passwords do not match.',
  }),

  billingCycleId: Joi.number()
    .valid(
      BillingCycle.WEEKLY,
      BillingCycle.BI_WEEKLY,
      BillingCycle.MONTHLY,
      BillingCycle.QUARTERLY,
      BillingCycle.ANNUALLY
    )
    .required()
    .messages({
      'any.only': 'Billing cycle is invalid.',
      'any.required': 'Billing cycle is required.',
      'number.base': 'Billing cycle is invalid.',
    }),
  currency: Joi.number().valid(Currency.PHP, Currency.USD).required().messages({
    'any.only': 'Currency is invalid.',
    'any.required': 'Currency is required.',
    'number.base': 'Currency is invalid.',
  }),
  pin: Joi.string()
    .trim()
    .length(4)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Pin is required.',
      'string.base': 'Pin is invalid.',
      'string.empty': 'Pin is invalid.',
      'string.pattern.base': 'Pin is invalid.',
      'string.length': 'Pin must have 4 characters only.',
    }),
});
